import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  useClerk,
} from "@clerk/clerk-react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import UserProvider from "./providers/UserProvider/UserProvider";
import useUser from "./providers/UserProvider/useUser";
import TokenProvider from "./providers/TokenProvider/TokenProvider";
import ApiProvider from "./providers/ApiProvider/ApiProvider";
import ToastProvider from "./ui/Toast/ToastProvider";
import Loader from "./ui/Loader/Loader";
import ErrorPage from "./components/ErrorPage/ErrorPage";
import Button from "./ui/Button/Button";
import { useEffect, Suspense } from "react";
import Onboarding from "./pages/Onboarding/Onboarding";
import urls from "./routes";
import FeatureFlagProvider from "./providers/FeatureFlagProvider/FeatureFlagProvider";
import { usePostHog } from "posthog-js/react";

function PostHogPageView() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    if (posthog) {
      posthog.capture("$pageview", {
        $current_url: window.location.href,
      });
    }
  }, [location, posthog]);

  return null;
}

function LoadingState() {
  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Loader />
    </div>
  );
}

function InternalRoot() {
  const { isReady, isError, tenant, user, signOut } = useUser();
  const { user: clerkUser } = useClerk();
  const { pathname } = useLocation();

  useEffect(() => {
    if (window.Intercom) {
      window.Intercom("boot", {
        api_base: "https://api-iam.intercom.io",
        app_id: "kk1qmxl3",
      });
    }
  }, []);

  useEffect(() => {
    if (user && clerkUser && window.Intercom) {
      window.Intercom("update", {
        app_id: "kk1qmxl3",
        api_base: "https://api-iam.intercom.io",
        user_hash: user.intercomHash ?? undefined,
        name: clerkUser.fullName ?? undefined,
        email: user.email ?? undefined,
        user_id: user.identityId ?? undefined,
        created_at: clerkUser.createdAt
          ? Math.round(clerkUser.createdAt?.getTime() / 1000)
          : undefined,
        signed_up_at: clerkUser.createdAt
          ? Math.round(clerkUser.createdAt?.getTime() / 1000)
          : undefined,
        company: tenant
          ? {
              id: tenant.id,
              name: tenant.name,
              created_at: Math.round(
                new Date(tenant.createdAt).getTime() / 1000,
              ),
            }
          : undefined,
      });
    }
  }, [clerkUser, tenant, user]);

  if (isError) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ErrorPage
          errorCode="500"
          title="Something went wrong"
          subtitle="We're having trouble loading your account."
        >
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Button variant="outline" onClick={() => signOut()}>
              Log Out
            </Button>
            <Button variant="primary" onClick={() => location.reload()}>
              Refresh
            </Button>
          </div>
        </ErrorPage>
      </div>
    );
  }

  if (!isReady || !tenant) {
    return <LoadingState />;
  }

  if (!tenant?.isOnboardingComplete) {
    if (pathname !== urls.home) {
      return <Navigate to={urls.home} />;
    }

    return <Onboarding />;
  }

  return (
    <Suspense fallback={<LoadingState />}>
      <Outlet />
    </Suspense>
  );
}
function Root() {
  const navigate = useNavigate();

  return (
    <>
      <PostHogPageView />
      <ToastProvider>
        <ClerkProvider
          routerPush={(to) => navigate(to)}
          routerReplace={(to) => navigate(to, { replace: true })}
          publishableKey={
            import.meta.env.VITE_REACT_APP_CLERK_PUBLISHABLE_KEY as string
          }
        >
          <SignedIn>
            <TokenProvider>
              <ApiProvider>
                <UserProvider>
                  <FeatureFlagProvider>
                    <InternalRoot />
                  </FeatureFlagProvider>
                </UserProvider>
              </ApiProvider>
            </TokenProvider>
          </SignedIn>
          <SignedOut>
            <Outlet />
          </SignedOut>
        </ClerkProvider>
      </ToastProvider>
    </>
  );
}

export default Root;
